import { useCallback, useRef, useState } from 'react';
import {
  AbortStreamFunction,
  ChatMessageChunk,
  ChatMessageChunkType,
  EntityContext,
  LlmMessage,
  useLlmChatService,
  useLlmConversationsService,
} from 'nekst-api';
import { useEventsPublisher } from 'event-bus';
import { useDataLoaderNew } from 'data-loader';
import { ChatMessage, ChatSessionContextValue } from './types';
import { consumeTransferState } from './chatTransferStore';
import { detachRelayListener } from './streamRelay';
import { useStreamRelay } from './useStreamRelay';

function createUserMessage(content: string, context?: EntityContext[]): ChatMessage {
  return {
    role: 'user',
    content,
    timestamp: new Date(),
    context: context && context.length > 0 ? context : undefined,
  };
}

function createAssistantMessage(content: string): ChatMessage {
  return {
    role: 'assistant',
    content,
    timestamp: new Date(),
  };
}

function createToolActivityMessage(displayMessage: string): ChatMessage {
  return {
    role: 'tool-activity',
    content: displayMessage,
    timestamp: new Date(),
  };
}

function createErrorMessage(error: string): ChatMessage {
  return {
    role: 'assistant',
    content: error,
    timestamp: new Date(),
    isError: true,
  };
}

function convertApiMessages(apiMessages: LlmMessage[]): ChatMessage[] {
  return apiMessages
    .filter(
      (msg): msg is LlmMessage & { content: string; role: 'user' | 'assistant' } =>
        msg.content !== null && (msg.role === 'user' || msg.role === 'assistant'),
    )
    .map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.createdAt,
      context: msg.context ?? undefined,
    }));
}

export const CONVERSATION_CREATED_EVENT = 'LLM_CONVERSATION_CREATED';
export const CONVERSATION_TITLE_GENERATED_EVENT = 'LLM_CONVERSATION_TITLE_GENERATED';
export const CONVERSATION_DELETED_EVENT = 'LLM_CONVERSATION_DELETED';
export const CONVERSATION_TITLE_UPDATED_EVENT = 'LLM_CONVERSATION_TITLE_UPDATED';

export function useChatSession(conversationId?: number): ChatSessionContextValue {
  const initialTransfer = useRef(
    conversationId ? consumeTransferState(conversationId) : null,
  );

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const transfer = initialTransfer.current;
    if (transfer) {
      let msgs = transfer.messages;
      if (transfer.streamingText) {
        msgs = [...msgs, createAssistantMessage(transfer.streamingText)];
      }
      return msgs;
    }
    return [];
  });
  const [streamingText, setStreamingText] = useState(
    () => initialTransfer.current?.streamingText ?? '',
  );
  const [isStreaming, setIsStreaming] = useState(
    () => initialTransfer.current?.isStreaming ?? false,
  );
  const [isToolExecuting, setIsToolExecuting] = useState(false);
  const [inputText, setInputText] = useState('');
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(
    () => initialTransfer.current?.conversationId ?? null,
  );
  const [selectedContext, setSelectedContext] = useState<EntityContext[]>(
    () => initialTransfer.current?.selectedContext ?? [],
  );
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);

  const abortRef = useRef<AbortStreamFunction | null>(null);
  const conversationIdRef = useRef<number | null>(
    initialTransfer.current?.conversationId ?? null,
  );
  const streamingTextRef = useRef(initialTransfer.current?.streamingText ?? '');

  const chatService = useLlmChatService();
  const conversationsService = useLlmConversationsService();
  const eventsPublisher = useEventsPublisher();

  const { loading: isLoadingConversation } = useDataLoaderNew(
    async () => {
      if (conversationId) {
        if (initialTransfer.current) {
          initialTransfer.current = null;
          return;
        }

        if (abortRef.current) {
          abortRef.current();
          abortRef.current = null;
        }

        const apiMessages = await conversationsService.getConversationMessages(conversationId);
        setMessages(convertApiMessages(apiMessages));
        streamingTextRef.current = '';
        setStreamingText('');
        setIsStreaming(false);
        setSelectedContext([]);
        setEditingMessageId(null);

        conversationIdRef.current = conversationId;
        setCurrentConversationId(conversationId);
        setIsToolExecuting(false);
        setInputText('');
      } else {
        if (abortRef.current) {
          abortRef.current();
          abortRef.current = null;
        }
        setMessages([]);
        streamingTextRef.current = '';
        setStreamingText('');
        setIsStreaming(false);
        setIsToolExecuting(false);
        setInputText('');
        setCurrentConversationId(null);
        conversationIdRef.current = null;
        setSelectedContext([]);
        setEditingMessageId(null);
      }
    },
    { dependencies: [conversationId] },
  );

  const finalizeStreamedMessage = useCallback(() => {
    const completedText = streamingTextRef.current;
    if (completedText) {
      setMessages((prev) => [...prev, createAssistantMessage(completedText)]);
    }
    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
  }, []);

  const handleChunk = useCallback((chunk: ChatMessageChunk) => {
    if (chunk.type === ChatMessageChunkType.TEXT_DELTA && chunk.text) {
      setIsToolExecuting(false);
      streamingTextRef.current += chunk.text;
      setStreamingText(streamingTextRef.current);
    } else if (chunk.type === ChatMessageChunkType.USER_MESSAGE_SAVED && chunk.messageId) {
      setMessages((prev) => {
        const lastUserIndex = prev.reduce<number>(
          (acc, msg, i) => (msg.role === 'user' ? i : acc),
          -1,
        );
        if (lastUserIndex === -1) {
          return prev;
        }
        const updated = [...prev];
        updated[lastUserIndex] = { ...updated[lastUserIndex], id: chunk.messageId };
        return updated;
      });
    } else if (chunk.type === ChatMessageChunkType.CONVERSATION_CREATED && chunk.conversation) {
      conversationIdRef.current = chunk.conversation.id;
      setCurrentConversationId(chunk.conversation.id);
      eventsPublisher.publish(CONVERSATION_CREATED_EVENT, { conversation: chunk.conversation });
    } else if (chunk.type === ChatMessageChunkType.TOOL_CALL && chunk.displayMessage) {
      setIsToolExecuting(true);
      const pendingText = streamingTextRef.current;
      if (pendingText) {
        streamingTextRef.current = '';
        setStreamingText('');
        setMessages((prev) => [
          ...prev,
          createAssistantMessage(pendingText),
          createToolActivityMessage(chunk.displayMessage!),
        ]);
      } else {
        setMessages((prev) => [...prev, createToolActivityMessage(chunk.displayMessage!)]);
      }
    } else if (chunk.type === ChatMessageChunkType.FINISH) {
      setIsToolExecuting(false);
      finalizeStreamedMessage();
    } else if (chunk.type === ChatMessageChunkType.TITLE_GENERATED && chunk.text) {
      if (conversationIdRef.current) {
        eventsPublisher.publish(CONVERSATION_TITLE_GENERATED_EVENT, {
          conversationId: conversationIdRef.current,
          title: chunk.text,
        });
      }
    } else if (chunk.type === ChatMessageChunkType.ERROR) {
      setIsToolExecuting(false);
      detachRelayListener();
      setMessages((prev) => [...prev, createErrorMessage(chunk.error || 'An error occurred')]);
      streamingTextRef.current = '';
      setStreamingText('');
      setIsStreaming(false);
    }
  }, [finalizeStreamedMessage]);

  const { startRelay, stopRelay } = useStreamRelay(
    handleChunk,
    initialTransfer.current?.isStreaming ?? false,
  );

  const stopGeneration = useCallback(() => {
    if (!isStreaming) {
      return;
    }

    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }

    detachRelayListener();
    stopRelay();
    setIsToolExecuting(false);

    setMessages((prev) => {
      const filtered = prev.filter((m) => m.role !== 'tool-activity');
      const pendingText = streamingTextRef.current;
      if (pendingText) {
        return [...filtered, createAssistantMessage(pendingText)];
      }
      return filtered;
    });

    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
  }, [isStreaming, stopRelay]);

  const sendMessage = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || isStreaming) {
      return;
    }

    const contextToSend = selectedContext.length > 0 ? selectedContext : undefined;
    setMessages((prev) => [...prev, createUserMessage(trimmed, contextToSend)]);
    setInputText('');
    setSelectedContext([]);
    setIsStreaming(true);
    streamingTextRef.current = '';
    setStreamingText('');

    const dispatcher = startRelay();

    if (conversationIdRef.current) {
      abortRef.current = chatService.sendMessage(
        conversationIdRef.current,
        { message: trimmed, context: contextToSend },
        dispatcher,
      );
    } else {
      abortRef.current = chatService.startConversation(
        { message: trimmed, context: contextToSend },
        dispatcher,
      );
    }
  }, [inputText, isStreaming, selectedContext, chatService, startRelay]);

  const editMessage = useCallback((messageId: number, newContent: string) => {
    if (isStreaming || !conversationIdRef.current) {
      return;
    }

    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) {
      return;
    }
    const newUserMessage = createUserMessage(newContent);

    setEditingMessageId(null);
    setMessages((prev) => [...prev.slice(0, messageIndex), newUserMessage]);
    setIsStreaming(true);
    streamingTextRef.current = '';
    setStreamingText('');

    const dispatcher = startRelay();
    abortRef.current = chatService.editMessage(
      conversationIdRef.current!,
      messageId,
      { message: newContent },
      dispatcher,
    );
  }, [isStreaming, messages, chatService, startRelay]);

  const resetConversation = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    stopRelay();
    setMessages([]);
    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
    setIsToolExecuting(false);
    setInputText('');
    setCurrentConversationId(null);
    conversationIdRef.current = null;
    setSelectedContext([]);
  }, [stopRelay]);

  const addContext = useCallback((context: EntityContext) => {
    setSelectedContext((prev) => {
      const exists = prev.some((c) => c.type === context.type && c.id === context.id);
      if (exists) {
        return prev;
      }
      return [...prev, context];
    });
  }, []);

  const removeContext = useCallback((context: EntityContext) => {
    setSelectedContext((prev) =>
      prev.filter((c) => !(c.type === context.type && c.id === context.id)),
    );
  }, []);

  const clearContext = useCallback(() => {
    setSelectedContext([]);
  }, []);

  return {
    messages,
    streamingText,
    isStreaming,
    isToolExecuting,
    isLoadingConversation: isLoadingConversation && !!conversationId && messages.length === 0,
    inputText,
    currentConversationId,
    selectedContext,
    editingMessageId,
    sendMessage,
    stopGeneration,
    editMessage,
    setEditingMessageId,
    resetConversation,
    setInputText,
    addContext,
    removeContext,
    clearContext,
  };
}
